import { useTheme, type ThemeNames } from '../../../theme';
import { Icon } from '@doxyme/icons';
import { useIntl } from '@doxyme/locale';
import { useStatusStyles } from './status.styles';
import { Spinner as SpinnerComponent, type SpinnerMode } from '../spinner';

import type { JSX } from 'react';

export type NotificationLevel =
  | 'success'
  | 'warning'
  | 'info'
  | 'error'
  | 'loader'
  | 'spinner';

type CommonProps = {
  color?: string;
  className?: string;
  size?: number;
  ariaLabels?: Partial<
    Record<
      'success' | 'warning' | 'info' | 'error' | 'spinner' | 'loader',
      string
    >
  >;
  themeName?: ThemeNames;
};

type DefaultProps = {
  statusLevel: Exclude<NotificationLevel, 'loader'>;
  value?: never;
  mode?: never;
};

type LoaderProps = {
  statusLevel: 'loader';
  value: number;
  mode?: SpinnerMode;
};

export type StatusProps = CommonProps & (DefaultProps | LoaderProps);

export const Status = ({
  statusLevel,
  color,
  className,
  ariaLabels = {},
  themeName,
  size = 24,
  value,
  mode = 'green',
}: StatusProps): JSX.Element | null => {
  const theme = useTheme(themeName);
  const { formatMessage } = useIntl();
  const styles = useStatusStyles();

  switch (statusLevel) {
    case 'success': {
      return (
        <Icon.Checkmark
          css={styles.icon}
          type='filled'
          size={size}
          color={color ?? theme.status.positive}
          className={className}
          aria-label={
            ariaLabels.success ?? formatMessage({ id: 'status.success' })
          }
          fill={theme.status.base}
        />
      );
    }
    case 'warning': {
      return (
        <Icon.Error
          css={styles.icon}
          type='filled'
          size={size}
          color={color ?? theme.status.notice}
          className={className}
          aria-label={
            ariaLabels.warning ?? formatMessage({ id: 'status.warning' })
          }
          fill={theme.status.base}
        />
      );
    }
    case 'info': {
      return (
        <Icon.Info
          css={styles.icon}
          type='filled'
          size={size}
          color={color ?? theme.status.informative}
          className={className}
          aria-label={ariaLabels.info ?? formatMessage({ id: 'status.info' })}
          fill={theme.status.base}
        />
      );
    }
    case 'error': {
      return (
        <Icon.Error
          css={styles.icon}
          type='filled'
          size={size}
          color={color ?? theme.status.negative}
          className={className}
          aria-label={ariaLabels.error ?? formatMessage({ id: 'status.error' })}
          fill={theme.status.base}
        />
      );
    }
    case 'spinner': {
      return (
        <SpinnerComponent
          disableShrink={false}
          size={size - 4}
          mode={mode}
          aria-label={
            ariaLabels.spinner ?? formatMessage({ id: 'status.loading' })
          }
        />
      );
    }
    case 'loader': {
      return (
        <SpinnerComponent
          value={value}
          variant='determinate'
          disableShrink={false}
          size={size - 4}
          mode={mode}
          aria-label={
            ariaLabels.loader ?? formatMessage({ id: 'status.loading' })
          }
        />
      );
    }
    default: {
      return null;
    }
  }
};
